import React from "react";
import Header from "../../components/Header";
import DashboardSidebar from "../../components/DashboardSidebar";
import CompanyChatPanel from "../../components/CompanyChatPanel";
import CandidatePrepChatPanel from "../../components/CandidatePrepChatPanel";
import { AgentTaskCard } from "../../components/AgentTaskCard";
import { AgentTaskDetail } from "../../components/AgentTaskDetail";
import { getAuth, onAuthStateChanged, signOut, updateProfile } from 'firebase/auth';
import { getDatabase, ref, get, set, remove, child } from "firebase/database";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { IconContext } from "react-icons";
import { FiPlus, FiGrid, FiList, FiClock, FiLock, FiUnlock, FiArchive, FiUsers, FiTrendingUp, FiEye, FiExternalLink, FiArrowRight, FiCamera, FiUpload, FiKey, FiTrash2, FiCopy, FiCreditCard, FiBarChart2, FiMapPin, FiFileText } from "react-icons/fi";
import { Link, useNavigate, useLocation } from "react-router-dom";
import rand from "random-key";
import "../App/App.css";
import "./Dashboard.css";
import InterviewsPage from "./Interviews";
import { DashboardPageProps, DashboardPageState, SandboxData, DashboardStats, User, InterviewData, CompanyStats, ChatSessionListItem, SandboxPlan } from "../../types";

function deriveSessionTitle(s: { title?: string; type?: string; plan?: { title?: string; language?: string; difficulty?: string } | null; messages?: { role: string; content: string }[] }): string {
    if (s.title) return s.title;
    if (s.plan?.title) {
        const parts: string[] = [];
        if (s.plan.language) parts.push(s.plan.language);
        if (s.plan.difficulty) parts.push(s.plan.difficulty);
        const suffix = parts.length > 0 ? ` (${parts.join(" · ")})` : "";
        return s.plan.title + suffix;
    }
    const firstUser = s.messages?.find((m) => m.role === "user");
    if (firstUser?.content) {
        const trimmed = firstUser.content.trim();
        const truncated = trimmed.slice(0, 50);
        return truncated + (trimmed.length > 50 ? "…" : "");
    }
    return "New chat";
}

// Create a wrapper component to use hooks
function DashboardPageWrapper(props: { darkMode: boolean }) {
    const navigate = useNavigate();
    const location = useLocation();
    return <DashboardPage {...props} navigate={navigate} location={location} />;
}

const OVERVIEW_PAGE_SIZE = 10;

class DashboardPage extends React.Component<DashboardPageProps, DashboardPageState> {
    private auth = getAuth();
    private db = getDatabase();
    private storage = getStorage();
    private profilePhotoInputRef: HTMLInputElement | null = null;
    private videoRef: HTMLVideoElement | null = null;
    private cameraStream: MediaStream | null = null;
    private overviewSentinelRef = React.createRef<HTMLDivElement>();
    private overviewScrollRef = React.createRef<HTMLDivElement>();
    private overviewObserver: IntersectionObserver | null = null;

    constructor(props: DashboardPageProps) {
        super(props);
        this.state = {
            user: null,
            login: false,
            loading: false,
            sandboxes: [],
            filteredSandboxes: [],
            searchTerm: "",
            filterType: "all",
            viewMode: "grid",
            stats: {
                total: 0,
                public: 0,
                private: 0,
                archived: 0,
                languages: {}
            },
            recentActivity: [],
            accountType: null, // Will be set after loading user data
            // Company-specific state
            interviews: [],
            filteredInterviews: [],
            companyStats: {
                totalInterviews: 0,
                activeInterviews: 0,
                completedInterviews: 0,
                totalCandidates: 0,
                averageScore: 0
            },
            companyInfo: null,
            profilePhotoLoading: false,
            profilePhotoError: null as string | null,
            showCameraModal: false,
            cameraError: null as string | null,
            chatSessions: [],
            activeChatSessionId: null,
            chatRefreshTrigger: 0,
            apiKeys: [] as { id: string; name: string; createdAt: string; masked: string }[],
            apiKeysLoading: false,
            showCreateKeyModal: false,
            newKeyName: '',
            createdKey: null as string | null,
            revokingKeyId: null as string | null,
            regeneratingKeyId: null as string | null,
            copySuccess: false,
            overviewInterviewsVisible: OVERVIEW_PAGE_SIZE,
            selectedTaskId: null as string | null,
        };

        this.logout = this.logout.bind(this);
        this.createNewSandbox = this.createNewSandbox.bind(this);
        this.createNewInterview = this.createNewInterview.bind(this);
        this.handleSearch = this.handleSearch.bind(this);
        this.handleFilter = this.handleFilter.bind(this);
        this.toggleViewMode = this.toggleViewMode.bind(this);
        this.loadUserSandboxes = this.loadUserSandboxes.bind(this);
        this.loadCompanyData = this.loadCompanyData.bind(this);
        this.loadUserData = this.loadUserData.bind(this);
        this.handleProfilePhotoFile = this.handleProfilePhotoFile.bind(this);
        this.startCamera = this.startCamera.bind(this);
        this.stopCamera = this.stopCamera.bind(this);
        this.capturePhoto = this.capturePhoto.bind(this);
        this.uploadProfilePhotoFile = this.uploadProfilePhotoFile.bind(this);
        this.loadChatSessions = this.loadChatSessions.bind(this);
        this.loadPrepChatSessions = this.loadPrepChatSessions.bind(this);
        this.handleSessionSelect = this.handleSessionSelect.bind(this);
        this.handleSessionDelete = this.handleSessionDelete.bind(this);
        this.handlePrepSessionDelete = this.handlePrepSessionDelete.bind(this);
        this.handleNewSession = this.handleNewSession.bind(this);
        this.handleSessionUpdate = this.handleSessionUpdate.bind(this);
        this.handleSaveSession = this.handleSaveSession.bind(this);
        this.handleSavePrepSession = this.handleSavePrepSession.bind(this);
        this.handleFetchSession = this.handleFetchSession.bind(this);
        this.handleFetchPrepSession = this.handleFetchPrepSession.bind(this);
        this.handleSaveResearchSession = this.handleSaveResearchSession.bind(this);
        this.handleRefineResearch = this.handleRefineResearch.bind(this);
        this.handleTaskCardClick = this.handleTaskCardClick.bind(this);
        this.handleTaskDetailClose = this.handleTaskDetailClose.bind(this);
        this.loadApiKeys = this.loadApiKeys.bind(this);
        this.createApiKey = this.createApiKey.bind(this);
        this.revokeApiKey = this.revokeApiKey.bind(this);
        this.regenerateApiKey = this.regenerateApiKey.bind(this);
        this.closeCreateKeyModal = this.closeCreateKeyModal.bind(this);
        this.goToCompanyInterviewsList = this.goToCompanyInterviewsList.bind(this);
    }

    /** Programmatic navigation — query-only Link clicks can fail to update; this always switches to the interviews list */
    goToCompanyInterviewsList(): void {
        this.props.navigate('/dashboard?view=interviews');
    }

    componentDidUpdate(prevProps: DashboardPageProps, prevState: DashboardPageState): void {
        const path = this.props.location?.pathname;
        const prevPath = prevProps.location?.pathname;
        const accountType = this.state.accountType;
        const prevAccountType = prevState.accountType;
        const justNavigatedToSettings = path === '/dashboard/settings' && prevPath !== path;
        const justBecameCompany = path === '/dashboard/settings' && accountType === 'company' && prevAccountType !== accountType;
        if ((justNavigatedToSettings || justBecameCompany) && accountType === 'company') {
            this.loadApiKeys();
        }
        if (prevState.filteredInterviews !== this.state.filteredInterviews) {
            this.setState({ overviewInterviewsVisible: OVERVIEW_PAGE_SIZE });
        }
        this.setupOverviewObserver();
    }

    setupOverviewObserver(): void {
        const view = new URLSearchParams(this.props.location?.search || "").get("view");
        if (view === "interviews") return;
        const sentinel = this.overviewSentinelRef.current;
        const root = this.overviewScrollRef.current;
        if (!sentinel || !root || this.state.filteredInterviews.length <= (this.state.overviewInterviewsVisible ?? OVERVIEW_PAGE_SIZE)) {
            this.overviewObserver?.disconnect();
            this.overviewObserver = null;
            return;
        }
        if (this.overviewObserver) return;
        this.overviewObserver = new IntersectionObserver(
            (entries) => {
                if (!entries[0]?.isIntersecting) return;
                this.setState((prev) => ({
                    overviewInterviewsVisible: Math.min(
                        (prev.overviewInterviewsVisible ?? OVERVIEW_PAGE_SIZE) + OVERVIEW_PAGE_SIZE,
                        prev.filteredInterviews.length
                    ),
                }));
            },
            { root, rootMargin: "100px", threshold: 0 }
        );
        this.overviewObserver.observe(sentinel);
    }

    componentWillUnmount(): void {
        this.stopCamera();
        this.overviewObserver?.disconnect();
        this.overviewObserver = null;
    }

    componentDidMount(): void {
        this.setupOverviewObserver();
        // Check authentication status
        onAuthStateChanged(this.auth, async (user) => {
            if (user) {
                // Get user account type
                try {
                    const dbRef = ref(this.db);
                    const snapshot = await get(child(dbRef, `users/${user.uid}`));
                    let accountType = 'job_seeker'; // default
                    let companyInfo = null;

                    if (snapshot.exists()) {
                        const userData = snapshot.val();
                        accountType = userData.accountType || 'job_seeker';
                        if (accountType === 'company') {
                            companyInfo = userData;
                        }
                    }

                    this.setState({
                        user: user as User,
                        login: true,
                        loading: false,
                        accountType: accountType,
                        companyInfo: companyInfo
                    }, () => {
                        // Load appropriate data based on account type
                        if (accountType === 'company') {
                            this.loadCompanyData();
                        } else {
                            this.loadUserSandboxes();
                            this.loadPrepChatSessions();
                        }
                    });
                } catch (error) {
                    console.error('Error loading user data:', error);
                    this.setState({
                        user: user as User,
                        login: true,
                        loading: false,
                        accountType: 'job_seeker'
                    }, () => {
                        this.loadUserSandboxes();
                        this.loadPrepChatSessions();
                    });
                }
            } else {
                this.setState({
                    login: false,
                    loading: false
                });
                // Redirect to login if not authenticated
                this.props.navigate("/login");
            }
        });
    }

    async loadUserData(): Promise<void> {
        // This method can be used to load user-specific data if needed
        await this.loadUserSandboxes();
    }

    handleProfilePhotoFile(e: React.ChangeEvent<HTMLInputElement>): void {
        const file = e.target.files?.[0];
        if (!file || !file.type.startsWith('image/')) return;
        e.target.value = '';
        this.uploadProfilePhotoFile(file);
    }

    async uploadProfilePhotoFile(file: File): Promise<void> {
        const uid = this.state.user?.uid;
        if (!uid) return;

        this.setState({ profilePhotoLoading: true, profilePhotoError: null });

        try {
            const photoRef = storageRef(this.storage, `profile-photos/${uid}`);
            await uploadBytes(photoRef, file);
            const photoURL = await getDownloadURL(photoRef);
            const currentUser = this.auth.currentUser;
            if (!currentUser) throw new Error('Not signed in');
            await updateProfile(currentUser, { photoURL });
            this.setState({
                user: { ...this.state.user!, photoURL },
                profilePhotoLoading: false,
                profilePhotoError: null
            });
            await set(ref(this.db, `users/${uid}/photoURL`), photoURL);
            if (this.state.companyInfo) {
                this.setState({ companyInfo: { ...this.state.companyInfo, photoURL } });
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to update profile photo';
            this.setState({
                profilePhotoLoading: false,
                profilePhotoError: message
            });
        }
    }

    startCamera(): void {
        this.setState({ cameraError: null });
        if (!navigator.mediaDevices?.getUserMedia) {
            this.setState({ cameraError: 'Camera not supported in this browser' });
            return;
        }
        navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } })
            .then((stream) => {
                this.cameraStream = stream;
                this.setState({ showCameraModal: true }, () => {
                    if (this.videoRef) {
                        this.videoRef.srcObject = stream;
                    }
                });
            })
            .catch((err) => {
                this.setState({
                    cameraError: err.name === 'NotAllowedError'
                        ? 'Camera access was denied'
                        : err.message || 'Could not access camera'
                });
            });
    }

    stopCamera(): void {
        if (this.cameraStream) {
            this.cameraStream.getTracks().forEach((t) => t.stop());
            this.cameraStream = null;
        }
        if (this.videoRef) {
            this.videoRef.srcObject = null;
        }
        this.setState({ showCameraModal: false, cameraError: null });
    }

    capturePhoto(): void {
        if (!this.videoRef || !this.cameraStream) return;
        const video = this.videoRef;
        if (!video.videoWidth || !video.videoHeight) return;
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(video, 0, 0);
        canvas.toBlob(
            (blob) => {
                if (!blob) return;
                const file = new File([blob], 'profile-photo.jpg', { type: 'image/jpeg' });
                this.uploadProfilePhotoFile(file);
                this.stopCamera();
            },
            'image/jpeg',
            0.9
        );
    }

    async loadUserSandboxes(): Promise<void> {
        const userId = this.state.user?.uid;
        if (!userId) return;

        const sandboxes: SandboxData[] = [];
        const stats: DashboardStats = {
            total: 0,
            public: 0,
            private: 0,
            archived: 0,
            languages: {}
        };

        try {
            // Load private sandboxes
            const privateSnapshot = await get(ref(this.db, `private-sandboxes/${userId}`));
            if (privateSnapshot.exists()) {
                const privateData = privateSnapshot.val();
                Object.keys(privateData).forEach(sessionId => {
                    const sandboxData = privateData[sessionId];
                    const sandbox: SandboxData = {
                        id: sessionId,
                        content: sandboxData.content || '',
                        language: sandboxData.language || 'Unknown',
                        createdon: sandboxData.createdon || '',
                        lastAccessed: sandboxData.lastAccessed || '',
                        isPrivate: true,
                        isArchived: false,
                        type: 'private'
                    };
                    sandboxes.push(sandbox);
                    stats.private++;
                    stats.total++;

                    // Count languages
                    const language = sandbox.language;
                    stats.languages[language] = (stats.languages[language] || 0) + 1;
                });
            }

            // Load public sandboxes via the user-sessions index (avoids fetching entire code-sessions node)
            const userSessionsSnapshot = await get(ref(this.db, `user-sessions/${userId}`));
            if (userSessionsSnapshot.exists()) {
                const userSessionData = userSessionsSnapshot.val();
                const sessionIds = Object.keys(userSessionData);
                await Promise.all(sessionIds.map(async (sessionId) => {
                    try {
                        const sandboxSnap = await get(ref(this.db, `code-sessions/${sessionId}`));
                        const sandboxData = sandboxSnap.exists()
                            ? sandboxSnap.val()
                            : userSessionData[sessionId];
                        const sandbox: SandboxData = {
                            id: sessionId,
                            content: sandboxData.content || '',
                            language: sandboxData.language || 'Unknown',
                            createdon: sandboxData.createdon || '',
                            lastAccessed: sandboxData.lastAccessed || '',
                            isPrivate: false,
                            isArchived: false,
                            type: 'public'
                        };
                        sandboxes.push(sandbox);
                        stats.public++;
                        stats.total++;

                        // Count languages
                        const language = sandbox.language;
                        stats.languages[language] = (stats.languages[language] || 0) + 1;
                    } catch (_) { /* skip missing sessions */ }
                }));
            }

            // Load archived sandboxes
            const archivedSnapshot = await get(ref(this.db, `archived-sandboxes/${userId}`));
            if (archivedSnapshot.exists()) {
                const archivedData = archivedSnapshot.val();
                Object.keys(archivedData).forEach(sessionId => {
                    const sandboxData = archivedData[sessionId];
                    const sandbox: SandboxData = {
                        id: sessionId,
                        content: sandboxData.content || '',
                        language: sandboxData.language || 'Unknown',
                        createdon: sandboxData.createdon || '',
                        lastAccessed: sandboxData.lastAccessed || '',
                        isPrivate: sandboxData.isPrivate || false,
                        isArchived: true,
                        type: 'archived',
                        archivedAt: sandboxData.archivedAt || new Date().toLocaleString()
                    };
                    sandboxes.push(sandbox);
                    stats.archived++;
                    stats.total++;

                    // Count languages
                    const language = sandbox.language;
                    stats.languages[language] = (stats.languages[language] || 0) + 1;
                });
            }

            // Sort by most recently edited/accessed first (descending)
            const parseTimestamp = (s: string | undefined): number => {
                if (!s) return 0;
                const val = typeof s === "object" && s !== null && "lastAccessed" in s
                    ? (s as { lastAccessed?: string }).lastAccessed
                    : s;
                if (!val || typeof val !== "string") return 0;
                const d = new Date(val);
                return isNaN(d.getTime()) ? 0 : d.getTime();
            };
            sandboxes.sort((a, b) => {
                const aTime = parseTimestamp(a.lastAccessed as string) || parseTimestamp(a.createdon);
                const bTime = parseTimestamp(b.lastAccessed as string) || parseTimestamp(b.createdon);
                return bTime - aTime;
            });

            this.setState({
                sandboxes,
                filteredSandboxes: sandboxes,
                stats
            });

        } catch (error) {
            console.error('Error loading sandboxes:', error);
        }
    }

    async loadCompanyData(): Promise<void> {
        const userId = this.state.user?.uid;
        if (!userId) return;

        const interviews: InterviewData[] = [];
        const stats: CompanyStats = {
            totalInterviews: 0,
            activeInterviews: 0,
            completedInterviews: 0,
            totalCandidates: 0,
            averageScore: 0
        };

        try {
            // Load company interviews
            const interviewsSnapshot = await get(ref(this.db, `company-interviews/${userId}`));
            if (interviewsSnapshot.exists()) {
                const interviewsData = interviewsSnapshot.val();
                Object.keys(interviewsData).forEach(interviewId => {
                    const interviewData = interviewsData[interviewId];
                    const interview: InterviewData = {
                        id: interviewId,
                        title: interviewData.title || 'Untitled Interview',
                        description: interviewData.description || '',
                        status: interviewData.status || 'draft',
                        createdAt: interviewData.createdAt || '',
                        updatedAt: interviewData.updatedAt || '',
                        candidateCount: interviewData.candidateCount || 0,
                        averageScore: interviewData.averageScore || 0,
                        language: interviewData.language || 'Python',
                        difficulty: interviewData.difficulty || 'Medium'
                    };
                    interviews.push(interview);
                    stats.totalInterviews++;

                    if (interview.status === 'active') {
                        stats.activeInterviews++;
                    } else if (interview.status === 'completed') {
                        stats.completedInterviews++;
                    }

                    stats.totalCandidates += interview.candidateCount;
                });
            }

            // Calculate average score
            if (stats.totalInterviews > 0) {
                const totalScore = interviews.reduce((sum, interview) => sum + interview.averageScore, 0);
                stats.averageScore = Math.round(totalScore / stats.totalInterviews);
            }

            // Sort by updated date
            interviews.sort((a, b) => {
                const aTime = new Date(a.updatedAt || a.createdAt || 0);
                const bTime = new Date(b.updatedAt || b.createdAt || 0);
                return bTime.getTime() - aTime.getTime();
            });

            this.setState({
                interviews,
                filteredInterviews: interviews,
                companyStats: stats
            });

            // Load credits balance for company
            try {
                const token = await this.auth.currentUser?.getIdToken();
                if (token) {
                    const res = await fetch(`${this.getApiBase()}/api/credits/balance`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    if (res.ok) {
                        const data = await res.json();
                        this.setState({ creditsBalance: data.balance });
                    }
                }
            } catch {
                // Ignore credits fetch errors
            }

            await this.loadChatSessions();
        } catch (error) {
            console.error('Error loading company data:', error);
        }
    }

    async loadChatSessions(): Promise<void> {
        const userId = this.state.user?.uid;
        if (!userId || this.state.accountType !== 'company') return;

        try {
            const sessionsSnapshot = await get(ref(this.db, `company-chat-sessions/${userId}`));
            const sessions: ChatSessionListItem[] = [];

            if (sessionsSnapshot.exists()) {
                const data = sessionsSnapshot.val();
                Object.keys(data).forEach((sessionId) => {
                    const s = data[sessionId];
                    sessions.push({
                        id: sessionId,
                        title: deriveSessionTitle(s),
                        updatedAt: s.updatedAt || s.createdAt || '',
                        language: s.plan?.language,
                        createdAt: s.createdAt || s.updatedAt || ''
                    });
                });
            }

            sessions.sort((a, b) => {
                const aTime = new Date(a.updatedAt || 0).getTime();
                const bTime = new Date(b.updatedAt || 0).getTime();
                return bTime - aTime;
            });

            this.setState({ chatSessions: sessions });
        } catch (error) {
            console.error('Error loading chat sessions:', error);
        }
    }

    async loadPrepChatSessions(): Promise<void> {
        const userId = this.state.user?.uid;
        if (!userId || this.state.accountType !== 'job_seeker') return;

        try {
            const sessionsSnapshot = await get(ref(this.db, `candidate-chat-sessions/${userId}`));
            const sessions: ChatSessionListItem[] = [];

            if (sessionsSnapshot.exists()) {
                const data = sessionsSnapshot.val();
                Object.keys(data).forEach((sessionId) => {
                    const s = data[sessionId];
                    sessions.push({
                        id: sessionId,
                        title: deriveSessionTitle(s),
                        updatedAt: s.updatedAt || s.createdAt || '',
                        createdAt: s.createdAt || s.updatedAt || '',
                        type: s.type === 'research' ? 'research' : 'chat',
                        taskId: s.taskId || undefined,
                    });
                });
            }

            sessions.sort((a, b) => {
                const aTime = new Date(a.updatedAt || 0).getTime();
                const bTime = new Date(b.updatedAt || 0).getTime();
                return bTime - aTime;
            });

            this.setState({ chatSessions: sessions });
        } catch (error) {
            console.error('Error loading prep chat sessions:', error);
        }
    }

    handleSessionSelect(sessionId: string): void {
        this.setState({ activeChatSessionId: sessionId });
    }

    handleNewSession(): void {
        this.setState({ activeChatSessionId: null });
    }

    async handleSessionDelete(sessionId: string): Promise<void> {
        const userId = this.state.user?.uid;
        if (!userId) return;

        try {
            await remove(ref(this.db, `company-chat-sessions/${userId}/${sessionId}`));
            const sessions = this.state.chatSessions.filter((s) => s.id !== sessionId);
            const wasActive = this.state.activeChatSessionId === sessionId;
            this.setState({
                chatSessions: sessions,
                activeChatSessionId: wasActive ? (sessions[0]?.id ?? null) : this.state.activeChatSessionId,
            });
        } catch (error) {
            console.error("Error deleting chat session:", error);
        }
    }

    async handlePrepSessionDelete(sessionId: string): Promise<void> {
        const userId = this.state.user?.uid;
        if (!userId) return;

        try {
            await remove(ref(this.db, `candidate-chat-sessions/${userId}/${sessionId}`));
            const sessions = this.state.chatSessions.filter((s) => s.id !== sessionId);
            const wasActive = this.state.activeChatSessionId === sessionId;
            this.setState({
                chatSessions: sessions,
                activeChatSessionId: wasActive ? (sessions[0]?.id ?? null) : this.state.activeChatSessionId,
            });
        } catch (error) {
            console.error("Error deleting prep chat session:", error);
        }
    }

    async handleSessionUpdate(sessionId: string, title: string): Promise<void> {
        const sessions = [...this.state.chatSessions];
        const existing = sessions.find((s) => s.id === sessionId);
        const updatedAt = new Date().toISOString();

        if (existing) {
            existing.title = title;
            existing.updatedAt = updatedAt;
        } else {
            sessions.unshift({ id: sessionId, title, updatedAt, createdAt: updatedAt });
        }

        sessions.sort((a, b) => {
            const aTime = new Date(a.updatedAt || 0).getTime();
            const bTime = new Date(b.updatedAt || 0).getTime();
            return bTime - aTime;
        });

        this.setState({ chatSessions: sessions, activeChatSessionId: sessionId });
    }

    async handleSaveSession(
        sessionId: string | null,
        data: { messages: { role: string; content: string }[]; plan: unknown; title?: string }
    ): Promise<string | null> {
        const userId = this.state.user?.uid;
        if (!userId) return null;

        const id = sessionId || rand.generate(10);
        const now = new Date().toISOString();

        try {
            let sessionData: Record<string, unknown> = {
                messages: data.messages,
                plan: data.plan,
                title: data.title ?? "New chat",
                updatedAt: now,
            };

            if (sessionId) {
                const existing = await get(ref(this.db, `company-chat-sessions/${userId}/${id}`));
                if (existing.exists()) {
                    const existingVal = existing.val();
                    sessionData = { ...existingVal, ...sessionData };
                    if (data.title === undefined) {
                        sessionData.title = existingVal.title || "New chat";
                    }
                }
            } else {
                sessionData.createdAt = now;
            }

            await set(ref(this.db, `company-chat-sessions/${userId}/${id}`), sessionData);
            await this.loadChatSessions();
            return id;
        } catch (error) {
            console.error("Error saving chat session:", error);
            return null;
        }
    }

    async handleFetchSession(
        sessionId: string
    ): Promise<{ messages: { role: string; content: string }[]; plan?: SandboxPlan | null } | null> {
        const userId = this.state.user?.uid;
        if (!userId) return null;

        try {
            const snapshot = await get(ref(this.db, `company-chat-sessions/${userId}/${sessionId}`));
            if (!snapshot.exists()) return null;
            const data = snapshot.val();
            return {
                messages: data.messages || [],
                plan: data.plan || null,
            };
        } catch (error) {
            console.error("Error fetching chat session:", error);
            return null;
        }
    }

    async handleSavePrepSession(
        sessionId: string | null,
        data: { messages: { role: string; content: string }[]; title?: string }
    ): Promise<string | null> {
        const userId = this.state.user?.uid;
        if (!userId) return null;

        const id = sessionId || rand.generate(10);
        const now = new Date().toISOString();

        try {
            let sessionData: Record<string, unknown> = {
                messages: data.messages,
                title: data.title ?? "New chat",
                updatedAt: now,
            };

            if (sessionId) {
                const existing = await get(ref(this.db, `candidate-chat-sessions/${userId}/${id}`));
                if (existing.exists()) {
                    const existingVal = existing.val();
                    sessionData = { ...existingVal, ...sessionData };
                    if (data.title === undefined) {
                        sessionData.title = existingVal.title || "New chat";
                    }
                }
            } else {
                sessionData.createdAt = now;
            }

            await set(ref(this.db, `candidate-chat-sessions/${userId}/${id}`), sessionData);
            await this.loadPrepChatSessions();
            return id;
        } catch (error) {
            console.error("Error saving prep chat session:", error);
            return null;
        }
    }

    async handleFetchPrepSession(
        sessionId: string
    ): Promise<{ messages: { role: string; content: string }[]; type?: string; taskId?: string } | null> {
        const userId = this.state.user?.uid;
        if (!userId) return null;

        try {
            const snapshot = await get(ref(this.db, `candidate-chat-sessions/${userId}/${sessionId}`));
            if (!snapshot.exists()) return null;
            const data = snapshot.val();
            if (data.type === "research") {
                return { messages: [], type: "research", taskId: data.taskId };
            }
            return {
                messages: data.messages || [],
            };
        } catch (error) {
            console.error("Error fetching prep chat session:", error);
            return null;
        }
    }

    async handleSaveResearchSession(taskId: string, title: string): Promise<string | null> {
        const userId = this.state.user?.uid;
        if (!userId) return null;

        const id = rand.generate(10);
        const now = new Date().toISOString();

        try {
            await set(ref(this.db, `candidate-chat-sessions/${userId}/${id}`), {
                type: "research",
                taskId,
                title,
                createdAt: now,
                updatedAt: now,
            });
            await this.loadPrepChatSessions();
            this.setState({ activeChatSessionId: id });
            return id;
        } catch (error) {
            console.error("Error saving research session:", error);
            return null;
        }
    }

    async handleRefineResearch(newTaskId: string): Promise<void> {
        const userId = this.state.user?.uid;
        const sessionId = this.state.activeChatSessionId;
        if (!userId || !sessionId) return;

        try {
            const sessionRef = ref(this.db, `candidate-chat-sessions/${userId}/${sessionId}`);
            const snapshot = await get(sessionRef);
            const existing = snapshot.exists() ? snapshot.val() : {};
            await set(sessionRef, {
                ...existing,
                type: "research",
                taskId: newTaskId,
                updatedAt: new Date().toISOString(),
            });
            await this.loadPrepChatSessions();
        } catch (error) {
            console.error("Error updating research session:", error);
        }
    }

    handleTaskCardClick(taskId: string): void {
        this.setState({ selectedTaskId: taskId });
    }

    handleTaskDetailClose(): void {
        this.setState({ selectedTaskId: null });
    }

    handleSearch(e: React.ChangeEvent<HTMLInputElement>): void {
        const searchTerm = e.target.value.toLowerCase();
        this.setState({ searchTerm }, () => {
            if (this.state.accountType === 'company') {
                this.filterInterviews();
            } else {
                this.filterSandboxes();
            }
        });
    }

    handleFilter(filterType: DashboardPageState['filterType']): void {
        this.setState({ filterType }, () => {
            if (this.state.accountType === 'company') {
                this.filterInterviews();
            } else {
                this.filterSandboxes();
            }
        });
    }

    filterInterviews(): void {
        const { interviews, searchTerm, filterType } = this.state;

        let filtered = interviews.filter(interview => {
            // Search filter
            const matchesSearch = searchTerm === "" ||
                interview.title?.toLowerCase().includes(searchTerm) ||
                interview.description?.toLowerCase().includes(searchTerm) ||
                interview.language?.toLowerCase().includes(searchTerm);

            // Status filter
            const matchesStatus = filterType === "all" ||
                interview.status === filterType;

            return matchesSearch && matchesStatus;
        });

        this.setState({ filteredInterviews: filtered });
    }

    filterSandboxes(): void {
        const { sandboxes, searchTerm, filterType } = this.state;

        let filtered = sandboxes.filter(sandbox => {
            // Search filter
            const matchesSearch = searchTerm === "" ||
                sandbox.content?.toLowerCase().includes(searchTerm) ||
                sandbox.language?.toLowerCase().includes(searchTerm) ||
                sandbox.id?.toLowerCase().includes(searchTerm);

            // Type filter
            const matchesType = filterType === "all" ||
                (filterType === "public" && !sandbox.isPrivate && !sandbox.isArchived) ||
                (filterType === "private" && sandbox.isPrivate && !sandbox.isArchived) ||
                (filterType === "archived" && sandbox.isArchived);

            return matchesSearch && matchesType;
        });

        this.setState({ filteredSandboxes: filtered });
    }

    toggleViewMode(): void {
        this.setState(prevState => ({
            viewMode: prevState.viewMode === "grid" ? "list" : "grid"
        }));
    }

    async createNewSandbox(): Promise<void> {
        const userId = this.state.user?.uid;
        if (!userId) return;

        const sessionId = rand.generate(10);
        const initialContent = '# Welcome to your new sandbox!\n\n# Start coding here...\n\nprint("Hello, World!")';
        const createdon = new Date().toISOString();
        const language = "Python";
        const language_id = 71;
        const mode = "python";

        try {
            // Create as private sandbox for authenticated users
            await set(ref(this.db, `private-sandboxes/${userId}/${sessionId}`), {
                content: initialContent,
                createdon: createdon,
                language: language,
                language_id: language_id,
                mode: mode,
                user: userId,
                lastAccessed: createdon
            });

            // Navigate to the new sandbox
            this.props.navigate(`/box/${sessionId}`);
        } catch (error) {
            console.error('Error creating new sandbox:', error);
        }
    }

    async createNewInterview(): Promise<void> {
        const userId = this.state.user?.uid;
        if (!userId) return;

        const interviewId = rand.generate(10);
        const createdAt = new Date().toISOString();
        const initialContent = '# Welcome to your Interview Sandbox!\n\n# This interview session is designed to evaluate coding skills.\n# Candidates can write code, run it, and collaborate in real-time.\n\n# Instructions:\n# 1. Solve the problem below\n# 2. Explain your approach\n# 3. Optimize if possible\n\nprint("Hello, Interview!")';

        try {
            const interviewData = {
                title: 'New Interview',
                description: 'A new coding interview session',
                status: 'draft',
                createdAt: createdAt,
                updatedAt: createdAt,
                candidateCount: 0,
                averageScore: 0,
                language: 'Python',
                difficulty: 'Medium',
                content: initialContent,
                createdBy: userId
            };

            await set(ref(this.db, `company-interviews/${userId}/${interviewId}`), interviewData);

            // Navigate to the interview editor
            this.props.navigate(`/company/interview/${interviewId}`);
        } catch (error) {
            console.error('Error creating new interview:', error);
        }
    }

    logout(): void {
        signOut(this.auth).then(() => {
            this.setState({ login: false });
            this.props.navigate("/");
        }).catch((error) => {
            console.error('Logout error:', error);
        });
    }

    getApiBase(): string {
        return (process.env.NODE_ENV === 'production' && (!process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL.includes('localhost')))
            ? 'https://interview-sandbox-production.up.railway.app'
            : (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000');
    }

    async loadApiKeys(): Promise<void> {
        const user = this.auth.currentUser;
        if (!user) return;
        this.setState({ apiKeysLoading: true });
        try {
            const token = await user.getIdToken();
            const res = await fetch(`${this.getApiBase()}/api/api-keys`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                this.setState({ apiKeys: data.keys || [] });
            }
        } catch (e) {
            console.error('Failed to load API keys:', e);
        } finally {
            this.setState({ apiKeysLoading: false });
        }
    }

    async createApiKey(): Promise<void> {
        const user = this.auth.currentUser;
        if (!user || !(this.state.newKeyName || '').trim()) return;
        try {
            const token = await user.getIdToken();
            const res = await fetch(`${this.getApiBase()}/api/api-keys`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ name: (this.state.newKeyName || '').trim() })
            });
            const data = await res.json();
            if (res.ok && data.key) {
                this.setState({ createdKey: data.key, newKeyName: '', showCreateKeyModal: true });
                this.loadApiKeys();
            } else {
                this.setState({ profilePhotoError: data.error || data.message || 'Failed to create key' });
            }
        } catch (e) {
            console.error('Failed to create API key:', e);
            this.setState({ profilePhotoError: 'Failed to create API key' });
        }
    }

    async revokeApiKey(keyHash: string): Promise<void> {
        const user = this.auth.currentUser;
        if (!user) return;
        this.setState({ revokingKeyId: keyHash });
        try {
            const token = await user.getIdToken();
            const res = await fetch(`${this.getApiBase()}/api/api-keys/${encodeURIComponent(keyHash)}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                this.setState((prev) => ({ apiKeys: (prev.apiKeys || []).filter((k: { id: string }) => k.id !== keyHash) }));
            }
        } catch (e) {
            console.error('Failed to revoke API key:', e);
        } finally {
            this.setState({ revokingKeyId: null });
        }
    }

    async regenerateApiKey(keyHash: string, keyName: string): Promise<void> {
        const user = this.auth.currentUser;
        if (!user) return;
        this.setState({ regeneratingKeyId: keyHash });
        try {
            const token = await user.getIdToken();
            const createRes = await fetch(`${this.getApiBase()}/api/api-keys`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ name: keyName + ' (regenerated)' })
            });
            const createData = await createRes.json();
            if (!createRes.ok || !createData.key) {
                this.setState({ profilePhotoError: createData.error || createData.message || 'Failed to regenerate key' });
                return;
            }
            const revokeRes = await fetch(`${this.getApiBase()}/api/api-keys/${encodeURIComponent(keyHash)}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (revokeRes.ok) {
                this.setState((prev) => ({ apiKeys: (prev.apiKeys || []).filter((k: { id: string }) => k.id !== keyHash) }));
            }
            this.setState({ createdKey: createData.key, newKeyName: '', showCreateKeyModal: true });
            this.loadApiKeys();
        } catch (e) {
            console.error('Failed to regenerate API key:', e);
            this.setState({ profilePhotoError: 'Failed to regenerate API key' });
        } finally {
            this.setState({ regeneratingKeyId: null });
        }
    }

    closeCreateKeyModal(): void {
        this.setState({ showCreateKeyModal: false, newKeyName: '', createdKey: null, copySuccess: false });
    }

    async archiveSandbox(sandbox: SandboxData): Promise<void> {
        const userId = this.state.user?.uid;
        if (!userId) return;

        const sessionId = sandbox.id;
        const archivedAt = new Date().toLocaleString();

        try {
            // Create archived sandbox data
            const archivedData = {
                ...sandbox,
                archivedAt: archivedAt,
                isArchived: true
            };

            // Add to archived-sandboxes
            await set(ref(this.db, `archived-sandboxes/${userId}/${sessionId}`), archivedData);

            // Remove from original location
            if (sandbox.isPrivate) {
                await remove(ref(this.db, `private-sandboxes/${userId}/${sessionId}`));
            } else {
                await remove(ref(this.db, `code-sessions/${sessionId}`));
            }

            // Reload sandboxes
            await this.loadUserSandboxes();
        } catch (error) {
            console.error('Error archiving sandbox:', error);
        }
    }

    async unarchiveSandbox(sandbox: SandboxData): Promise<void> {
        const userId = this.state.user?.uid;
        if (!userId) return;

        const sessionId = sandbox.id;

        try {
            // Create unarchived sandbox data
            const unarchivedData = {
                content: sandbox.content,
                language: sandbox.language,
                createdon: sandbox.createdon,
                lastAccessed: sandbox.lastAccessed,
                user: userId
            };

            // Add back to original location
            if (sandbox.isPrivate) {
                await set(ref(this.db, `private-sandboxes/${userId}/${sessionId}`), unarchivedData);
            } else {
                await set(ref(this.db, `code-sessions/${sessionId}`), unarchivedData);
            }

            // Remove from archived-sandboxes
            await remove(ref(this.db, `archived-sandboxes/${userId}/${sessionId}`));

            // Reload sandboxes
            await this.loadUserSandboxes();
        } catch (error) {
            console.error('Error unarchiving sandbox:', error);
        }
    }

    formatSandboxTimestamp(sandbox: SandboxData): string {
        const raw = typeof sandbox.lastAccessed === "object" && sandbox.lastAccessed !== null && "lastAccessed" in sandbox
            ? (sandbox.lastAccessed as { lastAccessed?: string }).lastAccessed
            : sandbox.lastAccessed || sandbox.createdon;
        if (!raw || typeof raw !== "string") return "Unknown";
        const d = new Date(raw);
        return isNaN(d.getTime()) ? raw : d.toLocaleString();
    }

    renderSandboxCard(sandbox: SandboxData): React.ReactElement {
        const { darkMode } = this.props;
        const preview = sandbox.content ?
            sandbox.content.substring(0, 100) + (sandbox.content.length > 100 ? '...' : '') :
            'No content';

        const timestamp = this.formatSandboxTimestamp(sandbox);

        const language = sandbox.language || 'Unknown';

        return (
            <div key={sandbox.id} className={`sandbox-card ${darkMode ? 'sandbox-card-dark' : ''} ${sandbox.isArchived ? 'sandbox-archived' : ''}`}>
                <div className="sandbox-card-header">
                    <div className="sandbox-privacy">
                        {sandbox.isPrivate ? <FiLock className="privacy-icon" /> : <FiUnlock className="privacy-icon" />}
                        <span className="privacy-text">{sandbox.isPrivate ? 'Private' : 'Public'}</span>
                        {sandbox.isArchived && <span className="archive-badge">Archived</span>}
                    </div>
                    <div className={`sandbox-language ${language === 'Unknown' ? 'language-unknown' : ''}`}>{language}</div>
                </div>

                <div className="sandbox-content">
                    <h3 className="sandbox-title">
                        <Link to={`/box/${sandbox.id}`} className="sandbox-link">
                            interviewsandbox.com/box/{sandbox.id}
                        </Link>
                    </h3>
                    <p className="sandbox-preview">{preview}</p>
                </div>

                <div className="sandbox-footer">
                    <div className="sandbox-timestamp">
                        <FiClock className="timestamp-icon" />
                        <span>{timestamp}</span>
                    </div>
                    <div className="sandbox-actions">
                        <button
                            className={`archive-btn ${sandbox.isArchived ? 'unarchive' : ''}`}
                            onClick={() => sandbox.isArchived ? this.unarchiveSandbox(sandbox) : this.archiveSandbox(sandbox)}
                            title={sandbox.isArchived ? 'Unarchive' : 'Archive'}
                        >
                            <FiArchive />
                            {sandbox.isArchived ? 'Unarchive' : 'Archive'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    renderSandboxList(sandbox: SandboxData): React.ReactElement {
        const { darkMode } = this.props;
        const preview = sandbox.content ?
            sandbox.content.substring(0, 150) + (sandbox.content.length > 150 ? '...' : '') :
            'No content';

        const timestamp = this.formatSandboxTimestamp(sandbox);

        const language = sandbox.language || 'Unknown';

        return (
            <div key={sandbox.id} className={`sandbox-list-item ${darkMode ? 'sandbox-list-item-dark' : ''} ${sandbox.isArchived ? 'sandbox-archived' : ''}`}>
                <div className="list-item-privacy">
                    {sandbox.isPrivate ? <FiLock className="privacy-icon" /> : <FiUnlock className="privacy-icon" />}
                    {sandbox.isArchived && <span className="archive-badge">Archived</span>}
                </div>

                <div className="list-item-content">
                    <div className="list-item-header">
                        <Link to={`/box/${sandbox.id}`} className="list-item-title">
                            interviewsandbox.com/box/{sandbox.id}
                        </Link>
                        <div className="list-item-meta">
                            <span className={`list-item-language ${language === 'Unknown' ? 'language-unknown' : ''}`}>{language}</span>
                            <span className="list-item-timestamp">
                                <FiClock className="timestamp-icon" />
                                {timestamp}
                            </span>
                        </div>
                    </div>
                    <p className="list-item-preview">{preview}</p>
                </div>

                <div className="list-item-actions">
                    <button
                        className={`archive-btn ${sandbox.isArchived ? 'unarchive' : ''}`}
                        onClick={() => sandbox.isArchived ? this.unarchiveSandbox(sandbox) : this.archiveSandbox(sandbox)}
                        title={sandbox.isArchived ? 'Unarchive' : 'Archive'}
                    >
                        <FiArchive />
                        {sandbox.isArchived ? 'Unarchive' : 'Archive'}
                    </button>
                </div>
            </div>
        );
    }

    renderInterviewCard(interview: InterviewData): React.ReactElement {
        const { darkMode } = this.props;
        const preview = interview.description ?
            interview.description.substring(0, 100) + (interview.description.length > 100 ? '...' : '') :
            'No description';

        const statusColor: { [key: string]: string } = {
            'draft': '#ffa500',
            'active': '#00ff00',
            'completed': '#0080ff'
        };

        return (
            <div key={interview.id} className={`interview-card ${darkMode ? 'interview-card-dark' : ''}`}>
                <div className="interview-card-header">
                    <div className="interview-status">
                        <span
                            className="status-indicator"
                            style={{ backgroundColor: statusColor[interview.status] }}
                        ></span>
                        <span className="status-text">{interview.status}</span>
                    </div>
                    <div className="interview-header-right">
                        <div className="interview-language">{interview.language}</div>
                        {interview.status === 'active' && (
                            <a
                                href={`/box/${interview.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="sandbox-link-icon"
                                title="Open interview sandbox"
                                onClick={() => {
                                    sessionStorage.setItem('darkMode', String(this.props.darkMode));
                                }}
                            >
                                <FiExternalLink />
                            </a>
                        )}
                    </div>
                </div>

                <div className="interview-content">
                    <h3 className="interview-title">
                        <Link to={`/company/interview/${interview.id}`} className="interview-link">
                            {interview.title}
                        </Link>
                    </h3>
                    <p className="interview-preview">{preview}</p>
                </div>

                <div className="interview-footer">
                    <div className="interview-stats">
                        <div className="stat-item">
                            <FiUsers className="stat-icon" />
                            <span>{interview.candidateCount} candidates</span>
                        </div>
                        <div className="stat-item">
                            <FiTrendingUp className="stat-icon" />
                            <span>{interview.averageScore}% avg</span>
                        </div>
                    </div>
                    <div className="interview-actions">
                        <Link to={`/company/interview/${interview.id}`} className="edit-btn">
                            <FiEye />
                            Edit
                        </Link>
                        <Link to={`/company/results/${interview.id}`} className="results-btn">
                            <FiUsers />
                            Results
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    renderCompanyStats(): React.ReactElement {
        const { darkMode } = this.props;
        const { companyStats, companyInfo } = this.state;
        const companyName = companyInfo?.companyName || 'Company';

        return (
            <div className={`interviewos-stats-container ${darkMode ? 'interviewos-stats-dark' : ''}`}>
                <div className="interviewos-hero">
                    <h2>Welcome, <span className="interviewos-hero-accent">{companyName}</span></h2>
                    <p>Here is what&apos;s happening with your recruitment funnel today.</p>
                </div>

                <div className="interviewos-metrics-grid">
                    <div className="interviewos-metric-card">
                        <div className="interviewos-metric-icon interviewos-metric-icon-cyan">
                            <FiUsers size={20} />
                        </div>
                        <div className="interviewos-metric-content">
                            <span className="interviewos-metric-label">Total Candidates</span>
                            <span className="interviewos-metric-value">{companyStats.totalCandidates.toLocaleString()}</span>
                            <span className="interviewos-metric-badge interviewos-badge-positive">—</span>
                        </div>
                    </div>
                    <div className="interviewos-metric-card">
                        <div className="interviewos-metric-icon interviewos-metric-icon-blue">
                            <FiClock size={20} />
                        </div>
                        <div className="interviewos-metric-content">
                            <span className="interviewos-metric-label">Pending Interviews</span>
                            <span className="interviewos-metric-value">{companyStats.activeInterviews}</span>
                            <span className="interviewos-metric-badge interviewos-badge-active">Active</span>
                        </div>
                    </div>
                    <div className="interviewos-metric-card">
                        <div className="interviewos-metric-icon interviewos-metric-icon-purple">
                            <FiTrendingUp size={20} />
                        </div>
                        <div className="interviewos-metric-content">
                            <span className="interviewos-metric-label">Avg. Score</span>
                            <span className="interviewos-metric-value">{companyStats.averageScore}%</span>
                            <span className="interviewos-metric-badge interviewos-badge-elite">Elite</span>
                        </div>
                    </div>
                    <div className="interviewos-metric-card">
                        <div className="interviewos-metric-icon interviewos-metric-icon-orange">
                            <FiBarChart2 size={20} />
                        </div>
                        <div className="interviewos-metric-content">
                            <span className="interviewos-metric-label">Completion Rate</span>
                            <span className="interviewos-metric-value">
                                {companyStats.totalInterviews > 0
                                    ? Math.round((companyStats.completedInterviews / companyStats.totalInterviews) * 100)
                                    : 0}%
                            </span>
                            <span className="interviewos-metric-badge interviewos-badge-neutral">—</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    /** Full company interviews view: stats + controls + interview list */
    renderCompanyInterviewsView(): React.ReactElement {
        const { darkMode } = this.props;
        const {
            filteredInterviews,
            searchTerm,
            filterType,
            viewMode,
            companyStats,
        } = this.state;

        return (
            <div className={`dashboard ${this.state.accountType === 'company' ? 'company-dashboard' : ''}`} id="dashboard-content">
                {this.state.creditsBalance === 0 && (
                    <div className={`credits-banner credits-banner-zero ${darkMode ? 'credits-banner-dark' : ''}`}>
                        <FiCreditCard size={24} />
                        <span>You're out of credits. <Link to="/dashboard/credits">Buy more</Link> to keep interviewing.</span>
                    </div>
                )}
                {this.renderCompanyStats()}
                <div className={`dashboard-controls ${darkMode ? 'dashboard-controls-dark' : ''}`}>
                    <div className="controls-left">
                        <div className="filter-buttons">
                            <button
                                className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
                                onClick={() => this.handleFilter('all')}
                            >
                                All ({companyStats.totalInterviews})
                            </button>
                            <button
                                className={`filter-btn ${filterType === 'active' ? 'active' : ''}`}
                                onClick={() => this.handleFilter('active')}
                            >
                                Active ({companyStats.activeInterviews})
                            </button>
                            <button
                                className={`filter-btn ${filterType === 'completed' ? 'active' : ''}`}
                                onClick={() => this.handleFilter('completed')}
                            >
                                Completed ({companyStats.completedInterviews})
                            </button>
                            <button
                                className={`filter-btn ${filterType === 'draft' ? 'active' : ''}`}
                                onClick={() => this.handleFilter('draft')}
                            >
                                Draft ({companyStats.totalInterviews - companyStats.activeInterviews - companyStats.completedInterviews})
                            </button>
                        </div>
                    </div>
                    <div className="controls-right">
                        <button
                            className="view-toggle-btn"
                            onClick={this.toggleViewMode}
                            title={`Switch to ${viewMode === 'grid' ? 'list' : 'grid'} view`}
                        >
                            <IconContext.Provider value={{ size: "1.2em" }}>
                                {viewMode === 'grid' ? <FiList /> : <FiGrid />}
                            </IconContext.Provider>
                        </button>
                        <button className="create-btn" onClick={this.createNewInterview}>
                            <FiPlus className="create-icon" />
                            New Interview
                        </button>
                    </div>
                </div>
                <div className={`interviews-section ${darkMode ? 'interviews-section-dark' : ''}`}>
                    {filteredInterviews.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-icon">🎯</div>
                            <h3>No interviews found</h3>
                            <p>
                                {searchTerm || filterType !== 'all'
                                    ? 'Try adjusting your search or filters'
                                    : 'Create your first interview to get started!'
                                }
                            </p>
                            {!searchTerm && filterType === 'all' && (
                                <button className="create-btn" onClick={this.createNewInterview}>
                                    <FiPlus className="create-icon" />
                                    Create Interview
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className={`interviews-container ${viewMode === 'grid' ? 'grid-view' : 'list-view'}`}>
                            {filteredInterviews.map(interview => this.renderInterviewCard(interview))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    /** Build pipeline activity items from recent interviews */
    buildPipelineActivity(): { id: string; title: string; desc: string; time: string }[] {
        const { interviews } = this.state;
        const items: { id: string; title: string; desc: string; time: string }[] = [];
        const sorted = [...interviews].sort((a, b) => {
            const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
            const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
            return bTime - aTime;
        });

        sorted.slice(0, 5).forEach((inv, i) => {
            const updated = new Date(inv.updatedAt || inv.createdAt || 0);
            const now = new Date();
            const diffMs = now.getTime() - updated.getTime();
            const diffH = Math.floor(diffMs / (1000 * 60 * 60));
            const diffM = Math.floor(diffMs / (1000 * 60));
            let time = 'Just now';
            if (diffM >= 60) time = `${diffH}h ago`;
            else if (diffM >= 1) time = `${diffM}m ago`;

            const title = inv.status === 'completed'
                ? 'Interview completed'
                : inv.status === 'active'
                    ? 'Interview in progress'
                    : 'Interview updated';
            const desc = inv.title || 'Untitled Interview';
            items.push({ id: `${inv.id}-${i}`, title, desc, time });
        });

        if (items.length === 0) {
            items.push({ id: 'empty', title: 'No activity yet', desc: 'Complete interviews to see pipeline trends', time: '' });
        }
        return items;
    }

    /** Overview for company: InterviewOS-style layout */
    renderOverviewForCompany(): React.ReactElement {
        const { darkMode } = this.props;
        const { filteredInterviews, companyStats } = this.state;
        const upcomingInterviews = filteredInterviews.slice(0, 2);
        const pipelineActivity = this.buildPipelineActivity();
        const avgScore = companyStats.averageScore;

        return (
            <div ref={this.overviewScrollRef} className={`dashboard interviewos-overview ${this.state.accountType === 'company' ? 'company-dashboard' : ''}`} id="dashboard-content">
                {this.state.creditsBalance === 0 && (
                    <div className={`credits-banner credits-banner-zero ${darkMode ? 'credits-banner-dark' : ''}`}>
                        <FiCreditCard size={24} />
                        <span>You&apos;re out of credits. <Link to="/dashboard/credits">Buy more</Link> to keep interviewing.</span>
                    </div>
                )}
                {this.renderCompanyStats()}

                <div className="interviewos-main-grid">
                    <div className="interviewos-main-left">
                        <section className="interviewos-upcoming">
                            <div className="interviewos-section-header">
                                <h3>Upcoming Interviews</h3>
                                <button type="button" className="interviewos-link-accent interviewos-text-btn" onClick={this.goToCompanyInterviewsList}>
                                    See all activity
                                </button>
                            </div>
                            {upcomingInterviews.length === 0 ? (
                                <div className="interviewos-empty-card">
                                    <p>No interviews yet. Create one to get started.</p>
                                    <button className="interviewos-btn-primary" onClick={this.createNewInterview}>
                                        <FiPlus className="interviewos-btn-icon" />
                                        New Interview
                                    </button>
                                </div>
                            ) : (
                                <div className="interviewos-upcoming-cards">
                                    {upcomingInterviews.map(interview => this.renderOverviewInterviewCard(interview))}
                                </div>
                            )}
                        </section>

                        <section className="interviewos-pipeline">
                            <h3>Pipeline Trends</h3>
                            <div className={`interviewos-pipeline-list ${darkMode ? 'interviewos-pipeline-dark' : ''}`}>
                                {pipelineActivity.map(item => (
                                    <div key={item.id} className="interviewos-pipeline-item">
                                        <div className="interviewos-pipeline-icon">
                                            <FiFileText size={16} />
                                        </div>
                                        <div className="interviewos-pipeline-content">
                                            <span className="interviewos-pipeline-title">{item.title}</span>
                                            <span className="interviewos-pipeline-desc">{item.desc}</span>
                                        </div>
                                        {item.time && <span className="interviewos-pipeline-time">{item.time}</span>}
                                    </div>
                                ))}
                            </div>
                        </section>
                    </div>

                    <div className="interviewos-main-right">
                        <section className={`interviewos-evaluation ${darkMode ? 'interviewos-evaluation-dark' : ''}`}>
                            <div className="interviewos-evaluation-header">
                                <h3>AI Evaluation</h3>
                                <span className="interviewos-evaluation-dot" />
                            </div>
                            <div className="interviewos-gauge-wrap">
                                <div className="interviewos-gauge">
                                    <svg viewBox="0 0 120 120" className="interviewos-gauge-svg">
                                        <circle cx="60" cy="60" r="54" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
                                        <circle
                                            cx="60" cy="60" r="54"
                                            fill="none"
                                            stroke="url(#interviewos-gauge-gradient)"
                                            strokeWidth="8"
                                            strokeLinecap="round"
                                            strokeDasharray={`${(avgScore / 100) * 339.3} 339.3`}
                                            transform="rotate(-90 60 60)"
                                        />
                                        <defs>
                                            <linearGradient id="interviewos-gauge-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                                <stop offset="0%" stopColor="#4FD1ED" />
                                                <stop offset="100%" stopColor="#1a5f7a" />
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                    <span className="interviewos-gauge-value">{avgScore}</span>
                                    <span className="interviewos-gauge-label">SCORE</span>
                                </div>
                            </div>
                            {/* Per-category trait bars removed — wire real scores from candidate-responses
                                when per-category averages (communication, problemSolving, technicalFit)
                                are stored on each interview. Until then we only show the overall score gauge. */}
                            <button type="button" className="interviewos-btn-breakdown" onClick={this.goToCompanyInterviewsList}>
                                Detailed Breakdown
                            </button>
                        </section>

                        <section className={`interviewos-reach ${darkMode ? 'interviewos-reach-dark' : ''}`}>
                            <h3>CANDIDATE REACH</h3>
                            <div className="interviewos-reach-map">
                                <div className="interviewos-reach-map-placeholder">
                                    <FiMapPin size={32} className="interviewos-reach-map-icon" />
                                    <span>Global</span>
                                </div>
                            </div>
                            <div className="interviewos-reach-list">
                                <p className="interviewos-reach-empty">
                                    Location data will appear here once candidates complete interviews.
                                </p>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        );
    }

    /** Compact interview card for overview (InterviewOS candidate-card style) */
    renderOverviewInterviewCard(interview: InterviewData): React.ReactElement {
        const { darkMode } = this.props;
        const statusLabel = interview.status === 'active' ? 'ACTIVE' : interview.status === 'completed' ? 'COMPLETED' : 'DRAFT';
        const timeLabel = interview.updatedAt
            ? (() => {
                const d = new Date(interview.updatedAt);
                const now = new Date();
                const isToday = d.toDateString() === now.toDateString();
                return isToday ? 'TODAY' : d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
            })()
            : '—';

        return (
            <div key={interview.id} className={`interviewos-upcoming-card ${darkMode ? 'interviewos-upcoming-card-dark' : ''}`}>
                <div className="interviewos-upcoming-card-top">
                    <div className="interviewos-upcoming-avatar">
                        {(interview.title || 'I').charAt(0).toUpperCase()}
                    </div>
                    <span className={`interviewos-upcoming-badge interviewos-upcoming-badge-${interview.status}`}>
                        {statusLabel}
                    </span>
                </div>
                <div className="interviewos-upcoming-card-body">
                    <h4>{interview.title || 'Untitled Interview'}</h4>
                    <p className="interviewos-upcoming-meta">{interview.language} • {interview.difficulty}</p>
                    <div className="interviewos-upcoming-tags">
                        <span className="interviewos-tag">{interview.language}</span>
                        <span className="interviewos-tag">{interview.difficulty}</span>
                    </div>
                </div>
                <div className="interviewos-upcoming-card-footer">
                    <Link to={`/company/interview/${interview.id}`} className="interviewos-upcoming-btn interviewos-upcoming-btn-edit">
                        Edit
                    </Link>
                    <Link to={`/company/results/${interview.id}`} className="interviewos-upcoming-btn interviewos-upcoming-btn-results">
                        Results
                    </Link>
                </div>
            </div>
        );
    }

    renderStats(): React.ReactElement {
        const { darkMode } = this.props;
        const { stats } = this.state;
        const topLanguages = Object.entries(stats.languages)
            .sort(([, a], [, b]) => (b as number) - (a as number))
            .slice(0, 3);

        return (
            <div className={`stats-container ${darkMode ? 'stats-container-dark' : ''}`}>
                <div className="stats-grid">
                    <div className="stat-card">
                        <h3>{stats.total}</h3>
                        <p>Total Sandboxes</p>
                    </div>
                    <div className="stat-card">
                        <h3>{stats.public}</h3>
                        <p>Public</p>
                    </div>
                    <div className="stat-card">
                        <h3>{stats.private}</h3>
                        <p>Private</p>
                    </div>
                    <div className="stat-card">
                        <h3>{stats.archived}</h3>
                        <p>Archived</p>
                    </div>
                </div>

                {topLanguages.length > 0 && (
                    <div className="top-languages">
                        <h4>Top Languages</h4>
                        <div className="language-list">
                            {topLanguages.map(([language, count]) => (
                                <div key={language} className="language-item">
                                    <span className="language-name">{language}</span>
                                    <span className="language-count">{count as number}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    renderSettings(darkMode: boolean): React.ReactElement {
        const { companyInfo, user } = this.state;
        const isCompany = this.state.accountType === 'company';

        return (
            <div className={`dashboard dashboard-settings ${darkMode ? 'dashboard-dark' : ''}`}>
                <div className="settings-header">
                    <h2>Settings</h2>
                    <p>Manage your account and preferences</p>
                </div>
                <div className="settings-sections">
                    <section className="settings-section">
                        <h3>Profile</h3>
                        <div className="settings-card">
                            <div className="settings-row settings-row-profile-photo">
                                <span className="settings-label">Profile picture</span>
                                <div className="settings-profile-photo">
                                    <div className="settings-profile-photo-preview">
                                        {user?.photoURL ? (
                                            <img src={user.photoURL} alt="Profile" />
                                        ) : (
                                            <span className="settings-profile-photo-initial">
                                                {(isCompany ? (companyInfo?.companyName || 'C') : (user?.displayName || user?.email?.[0] || 'U')).charAt(0).toUpperCase()}
                                            </span>
                                        )}
                                    </div>
                                    <div className="settings-profile-photo-actions">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            ref={(el) => { this.profilePhotoInputRef = el; }}
                                            onChange={this.handleProfilePhotoFile}
                                            className="settings-profile-photo-input"
                                            aria-label="Upload profile photo"
                                        />
                                        <button
                                            type="button"
                                            className="settings-profile-photo-btn"
                                            onClick={() => this.profilePhotoInputRef?.click()}
                                            disabled={this.state.profilePhotoLoading}
                                        >
                                            <FiUpload /> Upload photo
                                        </button>
                                        <button
                                            type="button"
                                            className="settings-profile-photo-btn"
                                            onClick={this.startCamera}
                                            disabled={this.state.profilePhotoLoading}
                                        >
                                            <FiCamera /> Take picture
                                        </button>
                                    </div>
                                    {this.state.cameraError && !this.state.showCameraModal && (
                                        <p className="settings-profile-photo-error">{this.state.cameraError}</p>
                                    )}
                                    {this.state.profilePhotoError && (
                                        <p className="settings-profile-photo-error">{this.state.profilePhotoError}</p>
                                    )}
                                    {this.state.profilePhotoLoading && (
                                        <p className="settings-profile-photo-loading">Updating…</p>
                                    )}
                                </div>
                            </div>
                            {this.state.showCameraModal && (
                                <div
                                    className="settings-camera-modal-overlay"
                                    onClick={this.stopCamera}
                                    onKeyDown={(e) => e.key === 'Escape' && this.stopCamera()}
                                    role="dialog"
                                    aria-modal="true"
                                    aria-labelledby="camera-modal-title"
                                >
                                    <div
                                        className="settings-camera-modal"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <h3 id="camera-modal-title" className="settings-camera-modal-title">Take a picture</h3>
                                        <div className="settings-camera-modal-video-wrap">
                                            <video
                                                ref={(el) => { this.videoRef = el; }}
                                                autoPlay
                                                playsInline
                                                muted
                                                className="settings-camera-modal-video"
                                            />
                                        </div>
                                        {this.state.cameraError && (
                                            <p className="settings-profile-photo-error">{this.state.cameraError}</p>
                                        )}
                                        <div className="settings-camera-modal-actions">
                                            <button
                                                type="button"
                                                className="settings-profile-photo-btn settings-camera-cancel"
                                                onClick={this.stopCamera}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                className="settings-profile-photo-btn settings-camera-capture"
                                                onClick={this.capturePhoto}
                                            >
                                                <FiCamera /> Capture
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="settings-row">
                                <span className="settings-label">Display name</span>
                                <span className="settings-value">
                                    {isCompany ? (companyInfo?.companyName || 'Company') : (user?.displayName || user?.email || '—')}
                                </span>
                            </div>
                            <div className="settings-row">
                                <span className="settings-label">Email</span>
                                <span className="settings-value">{user?.email || '—'}</span>
                            </div>
                            {isCompany && companyInfo?.companyName && (
                                <div className="settings-row">
                                    <span className="settings-label">Company</span>
                                    <span className="settings-value">{companyInfo.companyName}</span>
                                </div>
                            )}
                        </div>
                    </section>
                    <section className="settings-section">
                        <h3>Preferences</h3>
                        <div className="settings-card">
                            <div className="settings-row">
                                <span className="settings-label">Theme</span>
                                <span className="settings-value">Uses site dark/light toggle</span>
                            </div>
                        </div>
                    </section>
                    {isCompany && (
                        <section className="settings-section">
                            <h3>API Keys</h3>
                            <div className="settings-card">
                                <p className="settings-api-keys-desc">Use API keys to access your interview data from the CLI or scripts. Keys can only be copied when first created — if you lose one, use Regenerate to create a replacement. Run: <code>sandbox config set api-key &lt;key&gt;</code></p>
                                {this.state.apiKeysLoading ? (
                                    <p className="settings-value">Loading...</p>
                                ) : (
                                    <>
                                        <div className="settings-api-keys-list">
                                            {(this.state.apiKeys || []).length === 0 ? (
                                                <p className="settings-value">No API keys yet.</p>
                                            ) : (
                                                (this.state.apiKeys || []).map((k: { id: string; name: string; createdAt: string; masked: string }) => (
                                                    <div key={k.id} className="settings-api-key-row">
                                                        <span className="settings-api-key-name">{k.name}</span>
                                                        <span className="settings-api-key-masked">{k.masked}</span>
                                                        <span className="settings-api-key-date">{new Date(k.createdAt).toLocaleDateString()}</span>
                                                        <button
                                                            type="button"
                                                            className="settings-api-key-regenerate"
                                                            onClick={() => this.regenerateApiKey(k.id, k.name)}
                                                            disabled={this.state.regeneratingKeyId === k.id}
                                                            title="Create new key and revoke this one (use if you lost the key)"
                                                        >
                                                            {this.state.regeneratingKeyId === k.id ? 'Regenerating…' : 'Regenerate'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="settings-api-key-revoke"
                                                            onClick={() => this.revokeApiKey(k.id)}
                                                            disabled={this.state.revokingKeyId === k.id}
                                                            title="Revoke key"
                                                        >
                                                            <FiTrash2 /> Revoke
                                                        </button>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            className="settings-profile-photo-btn"
                                            onClick={() => this.setState({ showCreateKeyModal: true, createdKey: null })}
                                        >
                                            <FiKey /> Create API key
                                        </button>
                                    </>
                                )}
                            </div>
                            {this.state.showCreateKeyModal && (
                                <div className="settings-modal-overlay" onClick={this.closeCreateKeyModal}>
                                    <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
                                        {this.state.createdKey ? (
                                            <>
                                                <h3>API key created</h3>
                                                <p className="settings-key-warning">Copy this key now — it will never be shown again. Store it securely (e.g. <code>sandbox config set api-key &lt;key&gt;</code>).</p>
                                                <div className="settings-created-key-wrap">
                                                    <code className="settings-created-key">{this.state.createdKey}</code>
                                                    <button
                                                        type="button"
                                                        className="settings-profile-photo-btn settings-copy-btn"
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(this.state.createdKey || '');
                                                            this.setState({ copySuccess: true });
                                                            setTimeout(() => this.setState({ copySuccess: false }), 2000);
                                                        }}
                                                    >
                                                        <FiCopy /> {this.state.copySuccess ? 'Copied!' : 'Copy'}
                                                    </button>
                                                </div>
                                                <button type="button" className="settings-profile-photo-btn" onClick={this.closeCreateKeyModal}>Done</button>
                                            </>
                                        ) : (
                                            <>
                                                <h3>Create API key</h3>
                                                <label className="settings-label">Name (e.g. CLI, CI)</label>
                                                <input
                                                    type="text"
                                                    className="settings-input"
                                                    value={this.state.newKeyName}
                                                    onChange={(e) => this.setState({ newKeyName: e.target.value })}
                                                    placeholder="My CLI"
                                                />
                                                <div className="settings-modal-actions">
                                                    <button type="button" className="settings-profile-photo-btn" onClick={this.closeCreateKeyModal}>Cancel</button>
                                                    <button type="button" className="settings-profile-photo-btn" onClick={this.createApiKey} disabled={!(this.state.newKeyName || '').trim()}>Create</button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </section>
                    )}
                </div>
            </div>
        );
    }

    render(): React.ReactElement {
        const { darkMode } = this.props;
        const {
            login,
            loading,
            filteredSandboxes,
            filteredInterviews,
            searchTerm,
            filterType,
            viewMode,
            stats,
            companyStats,
            accountType
        } = this.state;

        if (!login) {
            return <div></div>; // Will redirect to login
        }

        const isCompany = accountType === 'company';
        const searchParams = new URLSearchParams(this.props.location?.search || '');
        const dashboardView = searchParams.get('view');

        return (
            <div className={`dashboard-wrapper ${darkMode ? 'dashboard-dark' : ''} ${isCompany ? 'company-dashboard-wrapper' : ''}`}>
                <DashboardSidebar
                    darkMode={darkMode}
                    accountType={isCompany ? 'company' : 'job_seeker'}
                    companyName={this.state.companyInfo?.companyName}
                    userName={this.state.user?.displayName || this.state.user?.email?.split('@')[0]}
                    profilePhotoUrl={this.state.user?.photoURL ?? undefined}
                    onLogout={this.logout}
                    searchValue={searchTerm}
                    onSearchChange={(value) => this.setState({ searchTerm: value }, () => {
                        if (isCompany) this.filterInterviews();
                        else this.filterSandboxes();
                    })}
                    searchPlaceholder={isCompany ? "Search interviews..." : "Search sandboxes..."}
                    chatSessions={this.state.chatSessions}
                    activeChatSessionId={this.state.activeChatSessionId}
                    onSessionSelect={this.handleSessionSelect}
                    onSessionDelete={isCompany ? this.handleSessionDelete : this.handlePrepSessionDelete}
                    onNewSession={this.handleNewSession}
                />

                <div className={`dashboard-main ${this.props.location?.pathname !== '/dashboard/settings' && this.props.location?.pathname !== '/interviews' ? 'company-dashboard-main' : ''}`}>
                    <Header darkMode={darkMode} />

                    {this.props.location?.pathname === '/dashboard/settings' ? (
                        this.renderSettings(darkMode)
                    ) : this.props.location?.pathname === '/interviews' ? (
                        <InterviewsPage darkMode={darkMode} history={{ push: this.props.navigate }} />
                    ) : isCompany ? (
                        <div className="company-dashboard-three-col">
                            <div className="company-chat-column">
                                <CompanyChatPanel
                                    darkMode={darkMode}
                                    companyInfo={this.state.companyInfo}
                                    userId={this.state.user?.uid ?? null}
                                    sessionId={this.state.activeChatSessionId}
                                    fetchSession={this.handleFetchSession}
                                    onSessionUpdate={this.handleSessionUpdate}
                                    onSaveSession={this.handleSaveSession}
                                    onInterviewCreated={() => {
                                        this.loadCompanyData();
                                        this.setState((prev) => ({ chatRefreshTrigger: (prev.chatRefreshTrigger ?? 0) + 1 }));
                                    }}
                                    onPlanUpdated={() => {}}
                                    refreshTrigger={this.state.chatRefreshTrigger}
                                    interviews={this.state.interviews?.map((i) => ({ id: i.id, title: i.title })) ?? []}
                                />
                            </div>
                            <div className="company-content-column">
                                {dashboardView !== 'interviews' ? this.renderOverviewForCompany() : this.renderCompanyInterviewsView()}
                            </div>
                        </div>
                    ) : (
                        <div className="company-dashboard-three-col">
                            <div className="company-chat-column">
                                <CandidatePrepChatPanel
                                    darkMode={darkMode}
                                    userId={this.state.user?.uid ?? null}
                                    sessionId={this.state.activeChatSessionId}
                                    fetchSession={this.handleFetchPrepSession}
                                    onSessionUpdate={this.handleSessionUpdate}
                                    onSaveSession={this.handleSavePrepSession}
                                    onResearchStarted={this.handleSaveResearchSession}
                                    onRefineResearch={this.handleRefineResearch}
                                    onSandboxCreated={() => {
                                        this.loadUserSandboxes();
                                        this.loadPrepChatSessions();
                                    }}
                                    sandboxes={this.state.filteredSandboxes.map((s) => ({
                                        id: s.id,
                                        title: s.content?.split("\n")[0]?.replace(/^#\s*/, "").trim() || "Untitled",
                                    }))}
                                />
                            </div>
                            <div className="company-content-column">
                        <div className={`dashboard ${isCompany ? 'company-dashboard' : ''}`} id="dashboard-content">
                            {/* Stats Section */}
                            {isCompany ? this.renderCompanyStats() : this.renderStats()}

                            {/* Controls Section */}
                            <div className={`dashboard-controls ${darkMode ? 'dashboard-controls-dark' : ''}`}>
                                <div className="controls-left">
                                    <div className="filter-buttons">
                                        {isCompany ? (
                                            <>
                                                <button
                                                    className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
                                                    onClick={() => this.handleFilter('all')}
                                                >
                                                    All ({companyStats.totalInterviews})
                                                </button>
                                                <button
                                                    className={`filter-btn ${filterType === 'active' ? 'active' : ''}`}
                                                    onClick={() => this.handleFilter('active')}
                                                >
                                                    Active ({companyStats.activeInterviews})
                                                </button>
                                                <button
                                                    className={`filter-btn ${filterType === 'completed' ? 'active' : ''}`}
                                                    onClick={() => this.handleFilter('completed')}
                                                >
                                                    Completed ({companyStats.completedInterviews})
                                                </button>
                                                <button
                                                    className={`filter-btn ${filterType === 'draft' ? 'active' : ''}`}
                                                    onClick={() => this.handleFilter('draft')}
                                                >
                                                    Draft ({companyStats.totalInterviews - companyStats.activeInterviews - companyStats.completedInterviews})
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
                                                    onClick={() => this.handleFilter('all')}
                                                >
                                                    All ({stats.total})
                                                </button>
                                                <button
                                                    className={`filter-btn ${filterType === 'public' ? 'active' : ''}`}
                                                    onClick={() => this.handleFilter('public')}
                                                >
                                                    Public ({stats.public})
                                                </button>
                                                <button
                                                    className={`filter-btn ${filterType === 'private' ? 'active' : ''}`}
                                                    onClick={() => this.handleFilter('private')}
                                                >
                                                    Private ({stats.private})
                                                </button>
                                                <button
                                                    className={`filter-btn ${filterType === 'archived' ? 'active' : ''}`}
                                                    onClick={() => this.handleFilter('archived')}
                                                >
                                                    Archived ({stats.archived})
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className="controls-right">
                                    <button
                                        className="view-toggle-btn"
                                        onClick={this.toggleViewMode}
                                        title={`Switch to ${viewMode === 'grid' ? 'list' : 'grid'} view`}
                                    >
                                        <IconContext.Provider value={{ size: "1.2em" }}>
                                            {viewMode === 'grid' ? <FiList /> : <FiGrid />}
                                        </IconContext.Provider>
                                    </button>

                                    <button className="create-btn" onClick={isCompany ? this.createNewInterview : this.createNewSandbox}>
                                        <FiPlus className="create-icon" />
                                        {isCompany ? 'New Interview' : 'New Sandbox'}
                                    </button>
                                </div>
                            </div>

                            {/* Content Section */}
                            {isCompany ? (
                                <div className={`interviews-section ${darkMode ? 'interviews-section-dark' : ''}`}>
                                    {filteredInterviews.length === 0 ? (
                                        <div className="empty-state">
                                            <div className="empty-icon">🎯</div>
                                            <h3>No interviews found</h3>
                                            <p>
                                                {searchTerm || filterType !== 'all'
                                                    ? 'Try adjusting your search or filters'
                                                    : 'Create your first interview to get started!'
                                                }
                                            </p>
                                            {!searchTerm && filterType === 'all' && (
                                                <button className="create-btn" onClick={this.createNewInterview}>
                                                    <FiPlus className="create-icon" />
                                                    Create Interview
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <div className={`interviews-container ${viewMode === 'grid' ? 'grid-view' : 'list-view'}`}>
                                            {filteredInterviews.map(interview => this.renderInterviewCard(interview))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <>
                                {this.state.selectedTaskId ? (
                                    <div className="agent-task-detail-wrapper">
                                        <AgentTaskDetail
                                            taskId={this.state.selectedTaskId}
                                            title={this.state.chatSessions.find((s) => s.taskId === this.state.selectedTaskId)?.title}
                                            darkMode={darkMode}
                                            onClose={this.handleTaskDetailClose}
                                        />
                                    </div>
                                ) : (
                                <>
                                {/* Recent section: research tasks for job seeker */}
                                {this.state.chatSessions.some((s) => s.type === "research") && (
                                    <section className="overview-recent-section">
                                        <div className="overview-recent-header">
                                            <h3>Recent</h3>
                                        </div>
                                        <div className={`overview-recent-list ${darkMode ? 'overview-recent-list-dark' : ''}`}>
                                            {this.state.chatSessions
                                                .filter((s) => s.type === "research" && s.taskId)
                                                .map((session) => (
                                                    <AgentTaskCard
                                                        key={session.id}
                                                        taskId={session.taskId!}
                                                        title={session.title}
                                                        darkMode={darkMode}
                                                        onClick={() => this.handleTaskCardClick(session.taskId!)}
                                                    />
                                                ))}
                                        </div>
                                    </section>
                                )}
                                <div className="sandboxes-section">
                                    {filteredSandboxes.length === 0 ? (
                                        <div className="empty-state">
                                            <div className="empty-icon">📝</div>
                                            <h3>No sandboxes found</h3>
                                            <p>
                                                {searchTerm || filterType !== 'all'
                                                    ? 'Try adjusting your search or filters'
                                                    : 'Create your first sandbox to get started!'
                                                }
                                            </p>
                                            {!searchTerm && filterType === 'all' && (
                                                <button className="create-btn" onClick={this.createNewSandbox}>
                                                    <FiPlus className="create-icon" />
                                                    Create Sandbox
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <div className={`sandboxes-container ${viewMode === 'grid' ? 'grid-view' : 'list-view'}`}>
                                            {filteredSandboxes.map(sandbox =>
                                                viewMode === 'grid'
                                                    ? this.renderSandboxCard(sandbox)
                                                    : this.renderSandboxList(sandbox)
                                            )}
                                        </div>
                                    )}
                                </div>
                                </>
                                )}
                                </>
                            )}
                        </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default DashboardPageWrapper;
